import React from 'react';
import { useTranslation } from 'react-i18next';
import { Garage } from '@1po/1po-bff-fe-spec/generated/garage/Garage';
import {
  OrderDeliveryType,
  PresentableOrderStatusType,
} from '@1po/1po-bff-fe-spec/generated/order/response/model/OrderItem';
import { useTheme } from 'styled-components';
import { CalendarAltIcon, HourglassHalfIcon, StickyNoteIcon, TruckIcon, UserIcon } from 'assets/icons';
import { getDeliveryTypeLabel } from 'domains/order/OrderDelivery';
import { OrderStatus } from 'domains/order/OrderStatus';
import { CardPanel, CardTitle, OrderDetailCard } from 'pages/MyOrdersPage/OrderCards/OrderCard.styled';
import { Box, Flex, Icon, MarginBox, Pipeline, Text } from 'UI';
import { textFormatter } from 'utils';

export const ClientInformationCard = ({
  garage,
  orderStatus,
  orderDate,
  deliveryTypes,
  orderMark,
}: {
  garage: Garage;
  orderStatus: PresentableOrderStatusType;
  orderDate: string;
  deliveryTypes: OrderDeliveryType[];
  orderMark: string | undefined;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <OrderDetailCard data-cy={'section-client'}>
      <CardTitle>
        <Text type="lg" bold>
          {t('my_orders.details.client_information', 'Client information')}
        </Text>
      </CardTitle>
      <Flex>
        {garage && (
          <>
            <Flex>
              <CardPanel>
                <Icon
                  IconComponent={UserIcon}
                  height={16}
                  width={16}
                  color={theme.color.brand_black}
                  display={'inline'}
                  mt={4}
                />
                <MarginBox ml={24}>
                  <Text type="base" bold>
                    {t('my_orders.details.client', 'Client')}
                  </Text>
                </MarginBox>
              </CardPanel>
              <CardPanel>
                <Text type="base" color="grey35" data-cy={'client-name'}>
                  {garage.name ?? ''}
                </Text>
              </CardPanel>
            </Flex>
            <Pipeline size={'100%'} />
          </>
        )}
        <Flex>
          <CardPanel>
            <Icon
              IconComponent={HourglassHalfIcon}
              height={16}
              width={16}
              color={theme.color.brand_black}
              display={'inline'}
              mt={4}
            />
            <MarginBox ml={24}>
              <Text type="base" bold>
                {t('my_orders.details.status', 'Status')}
              </Text>
            </MarginBox>
          </CardPanel>
          <CardPanel>
            <Box>
              <OrderStatus orderStatus={orderStatus} />
            </Box>
          </CardPanel>
        </Flex>
      </Flex>
      <Pipeline size={'100%'} horizontal />
      <Flex>
        <Flex>
          <CardPanel>
            <Icon
              IconComponent={CalendarAltIcon}
              height={16}
              width={16}
              color={theme.color.brand_black}
              display={'inline'}
              mt={4}
            />
            <MarginBox ml={24}>
              <Text type="base" bold>
                {t('order.order_detail.order_date', 'Order date:')}{' '}
              </Text>
            </MarginBox>
          </CardPanel>
          <CardPanel>
            <Text type="base" color="grey35" data-cy={'order-date'}>
              {textFormatter.formatDate(new Date(orderDate), {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </Text>
          </CardPanel>
        </Flex>
        <Pipeline size={'100%'} />
        {deliveryTypes && (
          <Flex data-cy={'section-client-delivery-method'}>
            <CardPanel>
              <Icon
                IconComponent={TruckIcon}
                height={16}
                width={16}
                color={theme.color.brand_black}
                display={'inline'}
                mt={4}
              />
              <MarginBox ml={24}>
                <Text type="base" bold>
                  {t('my_orders.details.delivery_method', 'Delivery method')}
                </Text>
              </MarginBox>
            </CardPanel>
            <CardPanel>
              <Text type="base" color="grey35">
                <Flex direction={'column'}>
                  {deliveryTypes.map((deliveryType, index) => (
                    <Flex key={deliveryType} data-cy={`delivery-method-${deliveryType}`}>
                      {getDeliveryTypeLabel(deliveryType)}
                      {index < deliveryTypes.length - 1 && ', '}
                    </Flex>
                  ))}
                </Flex>
              </Text>
            </CardPanel>
          </Flex>
        )}
      </Flex>
      {orderMark && (
        <>
          <Pipeline size={'100%'} horizontal />
          <Flex>
            <Flex>
              <CardPanel>
                <Icon
                  IconComponent={StickyNoteIcon}
                  height={16}
                  width={16}
                  color={theme.color.brand_black}
                  display={'inline'}
                  mt={4}
                />
                <MarginBox ml={24}>
                  <Text type="base" bold>
                    {t('order.order_detail.order_mark', 'Order mark')}
                  </Text>
                </MarginBox>
              </CardPanel>
              <CardPanel>
                <Text type="base" color="grey35" data-cy={'order-mark-value'}>
                  {orderMark}
                </Text>
              </CardPanel>
            </Flex>
            <Pipeline size={'100%'} />
            <Flex />
          </Flex>
        </>
      )}
    </OrderDetailCard>
  );
};
